/**
 * Normalização de telefone do paciente.
 *
 * Por que existe: o campo `phone` guarda TEXTO LIVRE digitado pela
 * recepção e importado do HiDoctor. Medindo a base real (22.885 fichas
 * com telefone), o campo contém coisas como:
 *
 *   "88999999999"            → o caso bom (83% já são só dígitos)
 *   "(88) 99999-9999"        → com máscara
 *   "88 9 99999999"          → espaçado
 *   "88999999999  88999999999" → DOIS telefones no mesmo campo
 *   "5588999999999"          → com DDI
 *   "999999999 Gerlania"     → telefone + nome de quem atende
 *   "Gerlania"               → sem dígito nenhum
 *
 * Por isso a busca por telefone e o vínculo do portal nunca casavam de
 * forma confiável: os dois lados comparavam strings de formatos diferentes.
 *
 * A saída é gravada no campo derivado `phoneDigits`, NUNCA sobrescrevendo
 * `phone` — o texto original carrega informação humana que a recepção usa
 * ("o 9999 é da vizinha") e é o que aparece na ficha.
 *
 * LIMITE CONHECIDO: ~27% da base tem 8 ou 9 dígitos, ou seja, número SEM
 * DDD (herança do HiDoctor, que era um sistema local). Não dá pra
 * recuperar sem chutar o DDD, e chutar significa casar o paciente errado
 * — exatamente o risco que este campo existe pra eliminar. Esses ficam
 * com `phoneDigits` ausente e simplesmente não casam por telefone.
 */

/** Comprimentos válidos no Brasil: fixo com DDD (10) e celular com DDD (11). */
const VALID_LENGTHS = [10, 11];

/**
 * Extrai o telefone canônico (só dígitos, com DDD) de um campo livre.
 *
 * Retorna `undefined` — nunca string vazia — quando não há telefone
 * utilizável. Isso importa: `createPatient` filtra `undefined` antes do
 * setDoc, então devolver `''` gravaria `phoneDigits: ''` em milhares de
 * fichas e poluiria o índice (além de fazer todas elas casarem entre si).
 */
export function normalizePhoneDigits(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;

  let digits = String(raw).replace(/\D/g, '');
  if (!digits) return undefined;

  // DDI do Brasil: 55 + 10 ou 55 + 11. Só remove quando o resto fica com
  // comprimento válido, pra não estragar um número que legitimamente
  // comece com 55 (DDD 55 é Santa Maria/RS).
  if (digits.length === 12 || digits.length === 13) {
    if (digits.startsWith('55') && VALID_LENGTHS.includes(digits.length - 2)) {
      digits = digits.slice(2);
    }
  }

  // Mais de um número no mesmo campo (ex.: 22 dígitos = dois celulares):
  // fica com o primeiro, que é o que a recepção digitou primeiro.
  if (digits.length > 11) {
    digits = digits.slice(0, 11);
  }

  if (!VALID_LENGTHS.includes(digits.length)) return undefined;

  return digits;
}

/** True quando o valor rende um telefone utilizável (com DDD). */
export function isUsablePhoneDigits(raw: string | null | undefined): boolean {
  return normalizePhoneDigits(raw) !== undefined;
}
